import { type Surface } from '../../common/types';

import { type SurfaceLoaderFactory } from './types';
import { dataIsSurface } from './utils';

const surfaceLoaderQuery = `
  query SurfaceByTypeAndKey($type: String!, $key: String!, $context: JSON) {
    surfacePlatform {
      surfaceV2(type: $type, key: $key, context: $context) {
        key
        type
        label
        locale
        channel
        description
        definitionVersion
        revision
        fields
        zones
      }
    }
  }
`;

export const createSurfaceLoader: SurfaceLoaderFactory =
	({ gqlEndpoint }) =>
	async ({ surfaceType, surfaceKey, contextResolver }) => {
		try {
			const context = typeof contextResolver === 'function' ? contextResolver() : contextResolver;

			const responsePromise = requestSurface(gqlEndpoint, surfaceType, surfaceKey, context);

			const surface = await parseSurfaceFromResponse(responsePromise);
			return {
				surface: {
					...surface,
				},
			};
		} catch (error: any) {
			return {
				surface: undefined,
				error: error,
			};
		}
	};

const requestSurface = async (
	gqlEndpoint: string,
	surfaceType: string,
	surfaceKey: string,
	context: Record<string, any> | undefined,
) => {
	const headers = new Headers();
	headers.append('Content-Type', 'application/json');

	const request = new Request(gqlEndpoint, {
		method: 'POST',
		credentials: 'include',
		mode: 'cors',
		headers,
		body: JSON.stringify({
			query: surfaceLoaderQuery,
			variables: {
				type: surfaceType,
				key: surfaceKey,
				context,
			},
		}),
	});

	return fetch(request);
};

const parseSurfaceFromResponse = async (promiseResponse: Promise<any>): Promise<Surface> => {
	const data = await promiseResponse
		.then((response) => {
			if (!response.ok) {
				return Promise.reject(
					Error(`Unexpected response - ${response.status} ${response.statusText}`),
				);
			}
			return response;
		})
		.then((response) => response.json())
		.then((json) => {
			if (json?.errors) {
				return Promise.reject(Error(`Graphql errors received - ${JSON.stringify(json.errors)}`));
			}

			return json?.data;
		});

	const surface = data?.surfacePlatform?.surfaceV2;

	if (!dataIsSurface(surface)) {
		return Promise.reject(Error('Received data could not be determined to be a valid surface'));
	}
	return surface;
};
